import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, X, Loader2, Truck, Clock, Scissors, Camera, ChevronDown, Trash2, Banknote, FileText, ExternalLink, Pencil } from "lucide-react";
import api, { fmtMXN, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import ProjectTabs from "@/components/ProjectTabs";
import WorksNav from "@/components/WorksNav";
import { toast } from "sonner";

export default function Machinery() {
  const { user } = useAuth();
  const { id: projectId } = useParams();
  const canManage = ["admin", "contador"].includes(user?.role);
  const [machines, setMachines] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [logMachine, setLogMachine] = useState(null);
  const [cutMachine, setCutMachine] = useState(null);
  const [reportMachine, setReportMachine] = useState(null);
  const [editMachine, setEditMachine] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [logs, setLogs] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [m, p] = await Promise.all([
        api.get("/machinery", { params: projectId ? { project_id: projectId } : {} }),
        api.get("/projects"),
      ]);
      setMachines(m.data);
      setProjects(p.data);
    } catch {
      toast.error("No se pudo cargar maquinaria");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  const toggleLogs = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    try {
      const { data } = await api.get(`/machinery/${id}/logs`);
      setLogs((l) => ({ ...l, [id]: data }));
    } catch { /* noop */ }
  };

  const removeLog = async (machineId, logId) => {
    if (!window.confirm("¿Eliminar este registro de horas?")) return;
    try {
      await api.delete(`/machinery/logs/${logId}`);
      const { data } = await api.get(`/machinery/${machineId}/logs`);
      setLogs((l) => ({ ...l, [machineId]: data }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar");
    }
  };

  return (
    <div data-testid="machinery-page">
      <PageHeader
        title={projectId ? "Maquinaria de la obra" : "Maquinaria en Renta"}
        subtitle="Bitácora de horas por horómetro y cortes que generan el gasto del proyecto"
        actions={
          <button onClick={() => setOpenNew(true)} data-testid="new-machine-btn" className="btn-primary px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold">
            <Plus className="w-4 h-4" /> Máquina
          </button>
        }
      />
      <div className="px-6 lg:px-8 py-6 space-y-4">
        {projectId ? <ProjectTabs projectId={projectId} active="machinery" /> : <WorksNav active="machinery" />}
        {loading ? (
          <div className="panel p-10 text-center text-slate-400">Cargando…</div>
        ) : machines.length === 0 ? (
          <div className="panel p-10 text-center text-slate-500" data-testid="machinery-empty">
            <Truck className="w-10 h-10 mx-auto mb-3 text-slate-600" />
            Sin maquinaria registrada. Agrega la primera máquina en renta.
          </div>
        ) : (
          machines.map((m) => (
            <div key={m.id} className="panel p-4" data-testid={`machine-${m.id}`}>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-slate-100">{m.name}</span>
                    <span className="px-1.5 py-0.5 rounded-sm text-[10px] truncate max-w-[160px] inline-block align-middle" title={`${m.project_code} — ${m.project_name}`} style={{ background: "rgba(99,102,241,0.12)", color: "#a5b4fc", border: "1px solid rgba(99,102,241,0.3)" }}>{m.project_name || m.project_code}</span>
                    <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-semibold ${m.status === "activa" ? "badge-success" : "badge-pending"}`}>{m.status.toUpperCase()}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">{m.provider_name} · <span className="font-mono" style={{ color: "var(--cyan)" }}>{fmtMXN(m.hourly_rate)}{({ dia: "/día", viaje: "/viaje", mes: "/mes" })[m.rental_type] || "/hr"}</span>
                    <span className="ml-1.5 px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase" style={{ background: "rgba(148,163,184,0.1)", color: "#94a3b8", border: "1px solid rgba(148,163,184,0.25)" }}>
                      {({ dia: "Por día", hora: "Por hora", viaje: "Por viaje", mes: "Por mes" })[m.rental_type] || "Horómetro"}{m.capacity_m3 ? ` · ${m.capacity_m3} m³` : ""}
                    </span>
                    {m.reports_count > 0 && (
                      <button type="button" data-testid={`reports-badge-${m.id}`} onClick={() => setReportMachine(m)}
                        className="ml-1.5 px-1.5 py-0.5 rounded-sm text-[9px] font-mono cursor-pointer hover:opacity-80"
                        style={m.reports_unsigned > 0
                          ? { background: "rgba(251,191,36,0.1)", color: "#fbbf24", border: "1px solid rgba(251,191,36,0.35)" }
                          : { background: "rgba(34,197,94,0.1)", color: "var(--green)", border: "1px solid rgba(34,197,94,0.3)" }}>
                        {m.reports_count} reporte{m.reports_count !== 1 ? "s" : ""}{m.reports_unsigned > 0 ? ` · ${m.reports_unsigned} sin firma` : " · firmados"}
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs font-mono">
                  <div className="text-right">
                    <div className="label-mono text-[9px]">{m.rental_type === "viaje" ? "Viajes totales" : ["dia", "mes"].includes(m.rental_type) ? "Días registrados" : "Horas totales"}</div>
                    <div className="text-slate-100 font-bold">{m.rental_type === "viaje" ? `${m.total_trips} viajes` : ["dia", "mes"].includes(m.rental_type) ? `${m.total_days} días` : `${m.total_hours} hrs`}</div>
                  </div>
                  <div className="text-right">
                    <div className="label-mono text-[9px]">Total pago</div>
                    <div className="font-bold" style={{ color: "var(--cyan)" }} data-testid={`total-pay-${m.id}`}>
                      {fmtMXN(m.rental_type === "mes" ? m.cuts_total : m.total_amount)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="label-mono text-[9px]">Sin corte</div>
                    <div className="font-bold" style={{ color: m.pending_amount > 0 ? "#fbbf24" : "var(--green)" }}>
                      {m.rental_type === "viaje" ? `${m.pending_trips} viajes` : ["dia", "mes"].includes(m.rental_type) ? `${m.pending_days} días` : `${m.pending_hours} hrs`} · {fmtMXN(m.pending_amount)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {m.status === "activa" && (
                    <button onClick={() => setLogMachine(m)} data-testid={`log-hours-${m.id}`} className="btn-primary px-3 py-1.5 rounded-sm text-[10px] uppercase tracking-widest flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" /> {m.rental_type === "viaje" ? "Registrar viajes" : ["dia", "mes"].includes(m.rental_type) ? "Registrar día" : "Registrar horas"}
                    </button>
                  )}
                  {(m.pending_amount > 0 || (m.rental_type === "mes" && m.status === "activa" && m.pending_days > 0)) && (
                    <button onClick={() => setCutMachine(m)} data-testid={`cut-${m.id}`} className="btn-ghost px-3 py-1.5 rounded-sm text-[10px] uppercase tracking-widest flex items-center gap-1.5" style={{ color: "var(--green)", borderColor: "var(--green)" }}>
                      <Scissors className="w-3.5 h-3.5" /> Corte
                    </button>
                  )}
                  {canManage && (
                    <button onClick={() => setEditMachine(m)} data-testid={`edit-${m.id}`} title="Editar máquina" className="btn-ghost p-1.5 rounded-sm text-slate-400">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {user?.role === "admin" && m.logs_count === 0 && (
                    <button
                      onClick={async () => {
                        if (window.confirm(`¿Eliminar "${m.name}"? No tiene registros de horas.`)) {
                          try { await api.delete(`/machinery/${m.id}`); toast.success("Máquina eliminada"); load(); }
                          catch (err) { toast.error(err.response?.data?.detail || "No se pudo eliminar"); }
                        }
                      }}
                      data-testid={`delete-machine-${m.id}`} title="Eliminar máquina (sin registros)"
                      className="btn-ghost p-1.5 rounded-sm" style={{ color: "var(--red)" }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {canManage && m.status === "activa" && (
                    <button onClick={async () => { if (window.confirm("¿Finalizar esta renta?")) { await api.patch(`/machinery/${m.id}`, { status: "finalizada" }); load(); } }} data-testid={`finish-${m.id}`} className="btn-ghost px-2.5 py-1.5 rounded-sm text-[10px] uppercase tracking-widest text-slate-400">
                      Finalizar
                    </button>
                  )}
                  <button onClick={() => setReportMachine(m)} data-testid={`report-${m.id}`} className="btn-ghost px-2.5 py-1.5 rounded-sm text-[10px] uppercase tracking-widest flex items-center gap-1.5 text-slate-300">
                    <FileText className="w-3.5 h-3.5" /> Reporte
                  </button>
                  <button onClick={() => toggleLogs(m.id)} data-testid={`toggle-logs-${m.id}`} className="btn-ghost p-1.5 rounded-sm text-slate-400">
                    <ChevronDown className={`w-4 h-4 transition-transform ${expanded === m.id ? "rotate-180" : ""}`} />
                  </button>
                </div>
              </div>
              {expanded === m.id && (
                <div className="mt-3 pt-3 space-y-1" style={{ borderTop: "1px solid var(--border)" }} data-testid={`logs-${m.id}`}>
                  <div className="label-mono text-[10px] mb-1">// BITÁCORA DE HORAS</div>
                  {(logs[m.id] || []).length === 0 && <div className="text-xs text-slate-500">Sin registros aún.</div>}
                  {(logs[m.id] || []).map((l) => (
                    <div key={l.id} className="flex items-center gap-3 text-xs flex-wrap py-1" style={{ borderBottom: "1px solid var(--border)" }}>
                      <span className="font-mono text-slate-400 w-20">{fmtDate(l.date)}</span>
                      <span className="font-mono text-slate-200">{l.trips != null ? `${l.trips} viajes` : l.days != null ? `${l.days} día` : `${l.hours} hrs`}</span>
                      {l.rate_label && <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-mono" style={{ background: "rgba(34,211,238,0.08)", color: "var(--cyan)", border: "1px solid rgba(34,211,238,0.3)" }}>{l.rate_label}</span>}
                      {l.material && <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-mono" style={{ background: "rgba(148,163,184,0.1)", color: "#94a3b8", border: "1px solid rgba(148,163,184,0.25)" }}>{l.material}</span>}
                      {l.hourometer_start != null && <span className="font-mono text-slate-500">({l.hourometer_start} → {l.hourometer_end})</span>}
                      <span className="font-mono font-bold" style={{ color: "var(--cyan)" }}>{fmtMXN(l.amount)}</span>
                      {l.photo_path && <Camera className="w-3.5 h-3.5" style={{ color: "var(--cyan)" }} title="Con foto de horómetro" />}
                      <span className="text-slate-500 flex-1 truncate">{l.notes}</span>
                      <span className="text-slate-500">{l.created_by_name}</span>
                      {l.cut_id ? (
                        <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold badge-success">EN CORTE</span>
                      ) : canManage && (
                        <button onClick={() => removeLog(m.id, l.id)} data-testid={`del-log-${l.id}`} className="p-1 rounded-sm" style={{ color: "var(--red)" }}><Trash2 className="w-3 h-3" /></button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      {openNew && <NewMachineModal projects={projects} defaultProjectId={projectId} onClose={() => setOpenNew(false)} onDone={() => { setOpenNew(false); load(); }} />}
      {logMachine && <LogHoursModal machine={logMachine} onClose={() => setLogMachine(null)} onDone={() => { setLogMachine(null); setExpanded(null); load(); }} />}
      {cutMachine && <CutModal machine={cutMachine} onClose={() => setCutMachine(null)} onDone={() => { setCutMachine(null); load(); }} />}
      {reportMachine && <ReportModal machine={reportMachine} onClose={() => setReportMachine(null)} />}
      {editMachine && <EditMachineModal machine={editMachine} onClose={() => setEditMachine(null)} onDone={() => { setEditMachine(null); load(); }} />}
    </div>
  );
}

const Overlay = ({ children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
    {children}
  </div>
);

function NewMachineModal({ projects, defaultProjectId, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", provider_name: "", project_id: defaultProjectId || "", rental_type: "horometro", capacity_m3: "", hourly_rate: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [extraRates, setExtraRates] = useState([]);
  const RATE_LABELS = { horometro: "Tarifa por hora MXN *", hora: "Tarifa por hora MXN *", dia: "Tarifa por día MXN *", viaje: "Tarifa por viaje MXN *", mes: "Renta mensual MXN *" };
  const rateLabel = RATE_LABELS[form.rental_type];
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/machinery", {
        ...form,
        hourly_rate: Number(form.hourly_rate),
        capacity_m3: form.capacity_m3 === "" ? null : Number(form.capacity_m3),
        rates: extraRates.filter((r) => r.label.trim() && Number(r.rate) > 0).map((r) => ({ label: r.label.trim(), rate: Number(r.rate) })),
      });
      toast.success("Máquina registrada");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar");
    } finally { setSaving(false); }
  };
  return (
    <Overlay onClose={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="w-full max-w-md panel-strong" data-testid="new-machine-modal">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div><div className="label-mono">// NUEVA MÁQUINA EN RENTA</div></div>
          <button type="button" onClick={onClose} className="btn-ghost p-1.5 rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-3">
          <div><label className="label-mono block mb-1.5">Máquina *</label>
            <input required data-testid="machine-name" placeholder="Ej. Retroexcavadora CAT 416" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
          <div><label className="label-mono block mb-1.5">Proveedor (quien la renta) *</label>
            <input required data-testid="machine-provider" value={form.provider_name} onChange={(e) => setForm({ ...form, provider_name: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
          <div><label className="label-mono block mb-1.5">Tipo de cobro *</label>
            <select required data-testid="machine-rental-type" value={form.rental_type} onChange={(e) => setForm({ ...form, rental_type: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full">
              <option value="horometro">Por horómetro (inicial → final)</option>
              <option value="hora">Por hora (captura directa de horas)</option>
              <option value="dia">Por día (tarifa fija diaria)</option>
              <option value="viaje">Por viaje (volteos / acarreos)</option>
              <option value="mes">Por mes (renta fija mensual)</option>
            </select></div>
          <div><label className="label-mono block mb-1.5">Capacidad m³ (opcional)</label>
            <input type="number" step="0.5" min="0" data-testid="machine-capacity" placeholder="Ej. 5, 7 o 14" value={form.capacity_m3} onChange={(e) => setForm({ ...form, capacity_m3: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label-mono block mb-1.5">Proyecto *</label>
              <select required data-testid="machine-project" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full">
                <option value="">— Selecciona —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.name}</option>)}
              </select></div>
            <div><label className="label-mono block mb-1.5">{rateLabel}</label>
              <input required type="number" min="1" step="0.01" data-testid="machine-rate" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="label-mono">Tarifas adicionales (aditamentos)</label>
              <button type="button" data-testid="add-rate-btn" onClick={() => setExtraRates([...extraRates, { label: "", rate: "" }])} className="btn-ghost px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest" style={{ color: "var(--cyan)" }}>+ Agregar</button>
            </div>
            {extraRates.length === 0 && <p className="text-[10px] text-slate-500">Ej. retroexcavadora: tarifa estándar con bote y otra tarifa "Martillo".</p>}
            {extraRates.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_120px_28px] gap-2 mb-1.5 items-center">
                <input data-testid={`rate-label-${i}`} placeholder="Ej. Martillo" value={r.label} onChange={(e) => setExtraRates(extraRates.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} className="input-cyber px-3 py-2 text-sm" />
                <input data-testid={`rate-value-${i}`} type="number" min="1" step="0.01" placeholder="$ MXN" value={r.rate} onChange={(e) => setExtraRates(extraRates.map((x, j) => (j === i ? { ...x, rate: e.target.value } : x)))} className="input-cyber px-3 py-2 text-sm" />
                <button type="button" data-testid={`rate-remove-${i}`} onClick={() => setExtraRates(extraRates.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-400 font-bold">×</button>
              </div>
            ))}
          </div>
          <div><label className="label-mono block mb-1.5">Notas</label>
            <input data-testid="machine-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-sm text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="machine-submit" className="btn-primary px-4 py-2 rounded-sm text-xs uppercase tracking-widest flex items-center gap-2">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />} Registrar
          </button>
        </div>
      </form>
    </Overlay>
  );
}

function LogHoursModal({ machine, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const rentalType = machine.rental_type || "horometro";
  const [form, setForm] = useState({ date: today, hourometer_start: "", hourometer_end: "", hours: "", trips: "", material: "", rate_label: "", notes: "" });
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);

  const hours = rentalType === "horometro"
    ? (form.hourometer_start !== "" && form.hourometer_end !== ""
        ? Math.round((Number(form.hourometer_end) - Number(form.hourometer_start)) * 100) / 100
        : null)
    : rentalType === "hora"
      ? (form.hours !== "" ? Number(form.hours) : null)
      : null;
  const trips = rentalType === "viaje" && form.trips !== "" ? Number(form.trips) : null;
  const effRate = (machine.rates || []).find((r) => r.label === form.rate_label)?.rate ?? machine.hourly_rate;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let photo_path = null;
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photo_path = data.id;
      }
      await api.post(`/machinery/${machine.id}/logs`, {
        date: form.date,
        hourometer_start: rentalType === "horometro" && form.hourometer_start !== "" ? Number(form.hourometer_start) : null,
        hourometer_end: rentalType === "horometro" && form.hourometer_end !== "" ? Number(form.hourometer_end) : null,
        hours: rentalType === "hora" && form.hours !== "" ? Number(form.hours) : null,
        trips: rentalType === "viaje" && form.trips !== "" ? Number(form.trips) : null,
        material: form.material || null,
        rate_label: form.rate_label || null,
        notes: form.notes || null,
        photo_path,
      });
      toast.success(rentalType === "viaje" ? "Viajes registrados" : ["dia", "mes"].includes(rentalType) ? "Día registrado" : "Horas registradas");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar");
    } finally { setSaving(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="w-full max-w-md panel-strong" data-testid="log-hours-modal">
        <div className="px-6 py-4 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="label-mono">{rentalType === "viaje" ? "// REGISTRAR VIAJES" : ["dia", "mes"].includes(rentalType) ? "// REGISTRAR DÍA" : "// REGISTRAR HORAS"}</div>
          <h2 className="font-display font-bold text-base text-slate-100 mt-1">{machine.name} · {fmtMXN(machine.hourly_rate)}{({ dia: "/día", viaje: "/viaje", mes: "/mes" })[rentalType] || "/hr"}</h2>
        </div>
        <div className="px-6 py-5 space-y-3">
          <div><label className="label-mono block mb-1.5">Fecha *</label>
            <input required type="date" data-testid="log-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
          {(machine.rates || []).length > 0 && (
            <div><label className="label-mono block mb-1.5">Tarifa / Aditamento</label>
              <select data-testid="log-rate-select" value={form.rate_label} onChange={(e) => setForm({ ...form, rate_label: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full">
                <option value="">Estándar · {fmtMXN(machine.hourly_rate)}</option>
                {machine.rates.map((r) => <option key={r.label} value={r.label}>{r.label} · {fmtMXN(r.rate)}</option>)}
              </select></div>
          )}
          {rentalType === "horometro" && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-mono block mb-1.5">Horómetro inicial *</label>
                <input required type="number" step="0.1" data-testid="log-hstart" value={form.hourometer_start} onChange={(e) => setForm({ ...form, hourometer_start: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
              <div><label className="label-mono block mb-1.5">Horómetro final *</label>
                <input required type="number" step="0.1" data-testid="log-hend" value={form.hourometer_end} onChange={(e) => setForm({ ...form, hourometer_end: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
            </div>
          )}
          {rentalType === "hora" && (
            <div><label className="label-mono block mb-1.5">Horas trabajadas *</label>
              <input required type="number" step="0.01" min="0.01" max="24" data-testid="log-hours-input" placeholder="Ej. 8" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
          )}
          {rentalType === "viaje" && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-mono block mb-1.5">Viajes realizados *</label>
                <input required type="number" step="1" min="1" data-testid="log-trips-input" placeholder="Ej. 6" value={form.trips} onChange={(e) => setForm({ ...form, trips: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
              <div><label className="label-mono block mb-1.5">Material</label>
                <input data-testid="log-material" placeholder="Ej. grava, escombro" value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
            </div>
          )}
          {rentalType === "dia" ? (
            <div className="px-3 py-2 rounded-sm text-xs font-mono flex justify-between" data-testid="log-preview"
              style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.35)" }}>
              <span style={{ color: "var(--green)" }}>1 día de renta</span>
              <b style={{ color: "var(--cyan)" }}>{fmtMXN(machine.hourly_rate)}</b>
            </div>
          ) : rentalType === "mes" ? (
            <div className="px-3 py-2 rounded-sm text-xs font-mono flex justify-between" data-testid="log-preview"
              style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.35)" }}>
              <span style={{ color: "var(--green)" }}>1 día registrado (control)</span>
              <b style={{ color: "var(--cyan)" }}>Renta fija {fmtMXN(machine.hourly_rate)}/mes</b>
            </div>
          ) : rentalType === "viaje" ? (trips != null && trips > 0 && (
            <div className="px-3 py-2 rounded-sm text-xs font-mono flex justify-between" data-testid="log-preview"
              style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.35)" }}>
              <span style={{ color: "var(--green)" }}>{trips} viajes</span>
              <b style={{ color: "var(--cyan)" }}>{fmtMXN(trips * effRate)}</b>
            </div>
          )) : hours != null && (
            <div className="px-3 py-2 rounded-sm text-xs font-mono flex justify-between" data-testid="log-preview"
              style={{ background: hours > 0 ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)", border: `1px solid ${hours > 0 ? "rgba(34,197,94,0.35)" : "rgba(239,68,68,0.35)"}` }}>
              <span style={{ color: hours > 0 ? "var(--green)" : "var(--red)" }}>{hours} horas</span>
              <b style={{ color: "var(--cyan)" }}>{hours > 0 ? fmtMXN(hours * effRate) : "—"}</b>
            </div>
          )}
          <div><label className="label-mono block mb-1.5">{rentalType === "horometro" ? "Foto del horómetro (opcional) 📷" : "Foto de evidencia (opcional) 📷"}</label>
            <input type="file" accept="image/*" capture="environment" data-testid="log-photo" onChange={(e) => setPhoto(e.target.files?.[0] || null)} className="input-cyber px-3 py-2 text-xs w-full" /></div>
          <div><label className="label-mono block mb-1.5">Notas</label>
            <input data-testid="log-notes" placeholder="Ej. excavación cimentación eje 4" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-sm text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="log-submit" className="btn-primary px-4 py-2 rounded-sm text-xs uppercase tracking-widest flex items-center gap-2">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clock className="w-3.5 h-3.5" />} Guardar
          </button>
        </div>
      </form>
    </Overlay>
  );
}

function CutModal({ machine, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [form, setForm] = useState({ period_start: monthStart, period_end: today });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/machinery/${machine.id}/cut`, form);
      const units = machine.rental_type === "viaje" ? `${data.total_trips} viajes` : machine.rental_type === "mes" ? "renta mensual" : machine.rental_type === "dia" ? `${data.total_days} días` : `${data.total_hours} hrs`;
      toast.success(`Corte generado: ${units} · ${fmtMXN(data.total_amount)} — gasto creado en el proyecto (pendiente de aprobación)`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar el corte");
    } finally { setSaving(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="w-full max-w-sm panel-strong" data-testid="cut-modal">
        <div className="px-6 py-4 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="label-mono">// CORTE DE HORAS</div>
          <h2 className="font-display font-bold text-base text-slate-100 mt-1">{machine.name}</h2>
          <p className="text-[11px] text-slate-400 mt-1">Sin corte actualmente: <b style={{ color: "#fbbf24" }}>{machine.rental_type === "viaje" ? `${machine.pending_trips} viajes` : ["dia", "mes"].includes(machine.rental_type) ? `${machine.pending_days} días` : `${machine.pending_hours} hrs`} · {machine.rental_type === "mes" ? `renta fija ${fmtMXN(machine.hourly_rate)}/mes` : fmtMXN(machine.pending_amount)}</b></p>
        </div>
        <div className="px-6 py-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label-mono block mb-1.5">Desde *</label>
              <input required type="date" data-testid="cut-start" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
            <div><label className="label-mono block mb-1.5">Hasta *</label>
              <input required type="date" data-testid="cut-end" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
          </div>
          <p className="text-[11px] text-slate-500 flex items-start gap-1.5">
            <Banknote className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--green)" }} />
            {machine.rental_type === "mes"
              ? <>Se creará un <b>gasto pendiente</b> por la renta mensual fija de <b>{fmtMXN(machine.hourly_rate)}</b> con el proveedor, marcado como "requiere factura".</>
              : <>Se creará un <b>gasto pendiente</b> en el proyecto con el proveedor y monto del corte, marcado como "requiere factura".</>}
          </p>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-sm text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="cut-submit" className="btn-primary px-4 py-2 rounded-sm text-xs uppercase tracking-widest flex items-center gap-2">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Scissors className="w-3.5 h-3.5" />} Generar corte
          </button>
        </div>
      </form>
    </Overlay>
  );
}

function ReportModal({ machine, onClose }) {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [cuts, setCuts] = useState([]);
  const [reports, setReports] = useState([]);
  const [form, setForm] = useState({ source: "range", cut_id: "", period_start: monthStart, period_end: today, operator_name: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, r] = await Promise.all([
          api.get(`/machinery/${machine.id}/cuts`),
          api.get(`/machinery/${machine.id}/reports`),
        ]);
        setCuts(c.data);
        setReports(r.data);
      } catch { /* noop */ }
    })();
  }, [machine.id]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/machinery/${machine.id}/reports`, {
        source: form.source,
        cut_id: form.source === "cut" ? form.cut_id : null,
        period_start: form.source === "range" ? form.period_start : null,
        period_end: form.source === "range" ? form.period_end : null,
        operator_name: form.operator_name,
      });
      toast.success("Reporte generado");
      navigate(`/machinery/reports/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar el reporte");
    } finally { setSaving(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="w-full max-w-md panel-strong" data-testid="report-modal">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="label-mono">// REPORTE DE RENTA</div>
            <h2 className="font-display font-bold text-base text-slate-100 mt-1">{machine.name}</h2>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost p-1.5 rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-3">
          <div><label className="label-mono block mb-1.5">Nombre del operador *</label>
            <input required data-testid="report-operator" placeholder="Ej. Juan Pérez" value={form.operator_name} onChange={(e) => setForm({ ...form, operator_name: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
          <div><label className="label-mono block mb-1.5">Periodo del reporte *</label>
            <select data-testid="report-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full">
              <option value="range">Por rango de fechas</option>
              <option value="cut" disabled={cuts.length === 0}>Por corte existente {cuts.length === 0 ? "(sin cortes)" : ""}</option>
            </select></div>
          {form.source === "cut" ? (
            <div><label className="label-mono block mb-1.5">Corte *</label>
              <select required data-testid="report-cut" value={form.cut_id} onChange={(e) => setForm({ ...form, cut_id: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full">
                <option value="">— Selecciona —</option>
                {cuts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {fmtDate(c.period_start)} → {fmtDate(c.period_end)} · {machine.rental_type === "viaje" ? `${c.total_trips || 0} viajes` : ["dia", "mes"].includes(machine.rental_type) ? `${c.total_days || 0} días` : `${c.total_hours} hrs`} · {fmtMXN(c.total_amount)}
                  </option>
                ))}
              </select></div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-mono block mb-1.5">Desde *</label>
                <input required type="date" data-testid="report-start" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
              <div><label className="label-mono block mb-1.5">Hasta *</label>
                <input required type="date" data-testid="report-end" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
            </div>
          )}
          {reports.length > 0 && (
            <div className="pt-2" style={{ borderTop: "1px solid var(--border)" }}>
              <div className="label-mono text-[10px] mb-1.5">// REPORTES ANTERIORES</div>
              <div className="space-y-1 max-h-36 overflow-y-auto">
                {reports.map((r) => (
                  <button key={r.id} type="button" data-testid={`open-report-${r.id}`} onClick={() => navigate(`/machinery/reports/${r.id}`)}
                    className="w-full flex items-center gap-2 text-xs px-2 py-1.5 rounded-sm btn-ghost text-left">
                    <FileText className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                    <span className="font-mono text-slate-300 flex-1">{fmtDate(r.period_start)} → {fmtDate(r.period_end)} · {r.operator_name}</span>
                    {r.signed_at ? (
                      <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold badge-success">FIRMADO</span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold badge-pending">SIN FIRMA</span>
                    )}
                    <ExternalLink className="w-3 h-3 text-slate-500 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-sm text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="report-submit" className="btn-primary px-4 py-2 rounded-sm text-xs uppercase tracking-widest flex items-center gap-2">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Generar reporte
          </button>
        </div>
      </form>
    </Overlay>
  );
}

function EditMachineModal({ machine, onClose, onDone }) {
  const RATE_LABELS = { horometro: "Tarifa por hora MXN *", hora: "Tarifa por hora MXN *", dia: "Tarifa por día MXN *", viaje: "Tarifa por viaje MXN *", mes: "Renta mensual MXN *" };
  const [form, setForm] = useState({ name: machine.name, hourly_rate: machine.hourly_rate, notes: machine.notes || "" });
  const [rates, setRates] = useState((machine.rates || []).map((r) => ({ label: r.label, rate: r.rate })));
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/machinery/${machine.id}`, {
        name: form.name,
        hourly_rate: Number(form.hourly_rate),
        notes: form.notes || null,
        rates: rates.filter((r) => String(r.label).trim() && Number(r.rate) > 0).map((r) => ({ label: String(r.label).trim(), rate: Number(r.rate) })),
      });
      toast.success("Máquina actualizada");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    } finally { setSaving(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="w-full max-w-md panel-strong" data-testid="edit-machine-modal">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="label-mono">// EDITAR MÁQUINA</div>
            <h2 className="font-display font-bold text-base text-slate-100 mt-1">{machine.name}</h2>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost p-1.5 rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-3">
          <div><label className="label-mono block mb-1.5">Nombre *</label>
            <input required data-testid="edit-machine-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
          <div><label className="label-mono block mb-1.5">{RATE_LABELS[machine.rental_type] || RATE_LABELS.hora}</label>
            <input required type="number" min="1" step="0.01" data-testid="edit-machine-rate" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="label-mono">Tarifas adicionales (aditamentos)</label>
              <button type="button" data-testid="edit-add-rate-btn" onClick={() => setRates([...rates, { label: "", rate: "" }])} className="btn-ghost px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest" style={{ color: "var(--cyan)" }}>+ Agregar</button>
            </div>
            {rates.length === 0 && <p className="text-[10px] text-slate-500">Ej. retroexcavadora: tarifa estándar con bote y otra tarifa "Martillo".</p>}
            {rates.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_120px_28px] gap-2 mb-1.5 items-center">
                <input data-testid={`edit-rate-label-${i}`} placeholder="Ej. Martillo" value={r.label} onChange={(e) => setRates(rates.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} className="input-cyber px-3 py-2 text-sm" />
                <input data-testid={`edit-rate-value-${i}`} type="number" min="1" step="0.01" placeholder="$ MXN" value={r.rate} onChange={(e) => setRates(rates.map((x, j) => (j === i ? { ...x, rate: e.target.value } : x)))} className="input-cyber px-3 py-2 text-sm" />
                <button type="button" data-testid={`edit-rate-remove-${i}`} onClick={() => setRates(rates.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-400 font-bold">×</button>
              </div>
            ))}
            <p className="text-[10px] text-slate-500 mt-1">Los cambios de tarifa aplican a registros nuevos; los ya guardados conservan su precio.</p>
          </div>
          <div><label className="label-mono block mb-1.5">Notas</label>
            <input data-testid="edit-machine-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" /></div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-sm text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="edit-machine-submit" className="btn-primary px-4 py-2 rounded-sm text-xs uppercase tracking-widest flex items-center gap-2">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pencil className="w-3.5 h-3.5" />} Guardar cambios
          </button>
        </div>
      </form>
    </Overlay>
  );
}
